import React, { useCallback, useState, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import "../styles/ProductSearchMail.css"; 
import { apiRequest } from "../utils/api";

const ProductSearchMail = ({ searchTerm, setSearchTerm, searchResults, setSearchResults, storeId }) => {
  const [editValues, setEditValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detailId, setDetailId] = useState(null);
  const [adjustmentsSell, setAdjustmentsSell] = useState([]);
  const [adjustmentsBuy, setAdjustmentsBuy] = useState([]);

  useEffect(() => {
    const fetchAdjustments = async () => {
      if (!detailId) return;
      try {
        const resSell = await apiRequest(`inventory/inventory_adjustments/?inventory_id=${detailId}&reason=販売&limit=10`);
        const resBuy = await apiRequest(`inventory/inventory_adjustments/?inventory_id=${detailId}&reason=仕入れ&limit=10`);
        setAdjustmentsSell(resSell.results || []);
        setAdjustmentsBuy(resBuy.results || []);
      } catch (error) {
        console.error("履歴取得失敗:", error);
      }
    };
    fetchAdjustments();
  }, [detailId]);

  const handleSearch = useCallback(async (page = 1) => {
    const keyword = searchTerm ?? "";
    if (!keyword.trim()) return;

    try {
      const response = await apiRequest(`inventory/inventory/?search=${keyword}&page=${page}`);
      const results = response.results || [];

      setSearchResults(results);
      setCurrentPage(page);
      setTotalPages(Math.ceil(response.count / 20)); // 1ページ20件想定

      const initialValues = {};
      results.forEach(product => {
        if (Array.isArray(product.store_inventory_set)) {
          const inv = product.store_inventory_set.find(i => String(i.store) === String(storeId));
          if (inv) {
            initialValues[inv.id] = {
              buy_price: inv.buy_price ?? 0,
              version_buy: inv.version_buy ?? 1,
              buy_top_switch: inv.buy_top_switch ?? false,
              buy_specialprice: inv.buy_specialprice ?? 0,
              buy_specialprice_switch: inv.buy_specialprice_switch ?? false,
              buy_specialprice_shoponly: inv.buy_specialprice_shoponly ?? false,
            };
          }
        }
      });

      setEditValues(initialValues);
    } catch (error) {
      console.error("検索エラー:", error);
    }
  }, [searchTerm, storeId, setSearchResults]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch(1); // 初期ページから検索
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTerm, handleSearch]);

  const handleInventoryChange = useCallback(async (storeInventoryId, updates, version) => {
    try {
      await apiRequest(`inventory/storeinventory/${storeInventoryId}/`, "PATCH", {
        ...updates,
        version_buy: version ?? 1,
      });
    } catch (error) {
      console.error("更新失敗:", error);
    }
  }, []);

  const debouncedInventoryChange = useMemo(() =>
    debounce((id, updates, version) => {
      handleInventoryChange(id, updates, version);
    }, 500), [handleInventoryChange]
  );

  useEffect(() => {
    return () => debouncedInventoryChange.cancel();
  }, [debouncedInventoryChange]);

  const productItem = detailId
  ? searchResults.find(p => p.id === detailId)
  : null;

  const openAmiamiWindow = (jan) => {
    const url = `https://slist.amiami.jp/top/search/list9?s_keywords=${jan}&submit=検索&pagemax=20&s_buy_flg=1`;
    const features = "width=400,height=800,toolbar=no,menubar=no,scrollbars=yes,resizable=yes";
    window.open(url, "_blank", features);
  };
  const opensurugayaWindow = (product, editValues = {}) => {
    const competitorProductId =
      editValues[product.id]?.competitor_productid ??
      product.competitorstore_set?.find((inv) => inv.store_name === "駿河屋")?.competitor_productid;
  
    let searchWord;
  
    if (competitorProductId) {
      searchWord = competitorProductId;
    } else if (!product.jan || product.jan === "") {
      searchWord = product.item_name;
    } else if (product.jan.length === 18) {
      searchWord = product.jan.slice(-9);
    } else if (product.jan.startsWith("20137")) {
      searchWord = product.item_name;
    } else {
      searchWord = product.jan;
    }
  
    const url = `https://www.suruga-ya.jp/kaitori/search_buy?category=&search_word=${encodeURIComponent(searchWord)}&searchbox=1`;
    const features = "width=400,height=800,toolbar=no,menubar=no,scrollbars=yes,resizable=yes";
    window.open(url, "_blank", features);
  };
  const openamazonWindow = (jan) => {
    const url = `https://www.amazon.co.jp/s?k=${jan}`;
    const features = "width=400,height=800,toolbar=no,menubar=no,scrollbars=yes,resizable=yes";
    window.open(url, "_blank", features);
  };
  const openmercariWindow = (item_name) => {
    const url = `https://jp.mercari.com/search?keyword=${item_name}&item_condition_id=1&sort=score&order=desc&status=on_sale`;
    const features = "width=400,height=800,toolbar=no,menubar=no,scrollbars=yes,resizable=yes";
    window.open(url, "_blank", features);
  };

  return (
    <div className="ProductSearchMail-container">
      <div className="section-title-container">
        <h2 className="ProductSearchMail-section-title">商品検索</h2>
        <label
          className="ProductSearchMail-reload-btn"
          title="再読み込み"
          onClick={() => handleSearch(currentPage)}
        >
          🔄
        </label>
      </div>

      <div className="ProductSearchMail-search-bar">
        <input
          type="text"
          placeholder="商品名/JAN/SKUを入力"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className="ProductSearchMail-clear-btn"
            onClick={() => {
              setSearchTerm("");
              setSearchResults([]);
              setEditValues({});
              setCurrentPage(1);
              setDetailId(null);
            }}
          >
            ✖
          </button>
        )}
      </div>

      {detailId ? (
        productItem ? (
          <>
            <button onClick={() => setDetailId(null)} className="ProductSearchMail-back-btn">← 戻る</button>
            {[productItem].map((product) => {
              const inv = Array.isArray(product.store_inventory_set)
                ? product.store_inventory_set.find(i => String(i.store) === String(storeId))
                : null;
              const edit = inv ? editValues[inv.id] : null;

              if (!inv) {
                return (
                  <div key={product.id} className="ProductSearchMail-detailitem">
                    <p className="ProductSearchMail-no-stock">⚠ この店舗に登録がありません</p>
                  </div>
                );
              }

              return (
                <div key={product.id} className="ProductSearchMail-detailitem">
                  <div className="ProductSearchMail-detail-upper">
                    <div className="product-search-imgbox">
                      <img
                        src={`/media/product_images/${product.sku}.jpg`}
                        alt={product.item_name}
                        className="ProductSearchMail-image"
                      />
                    </div>

                    <div className="ProductSearchMail-info">
                      <p className="ProductSearchMail-name">{product.item_name}</p>
                      <p className="ProductSearchMail-barcode">
                        JAN: {product.jan || "未登録"}　SKU: {product.sku || "未登録"}
                      </p>

                      {/* 1行目 */}
                      <div className="ProductSearchMail-line">
                        <span>買取:</span>
                        <label className="ProductSearchMail-label">
                          <input
                            type="number"
                            className="ProductSearchMail-input"
                            value={edit?.buy_price ?? ""}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              setEditValues((prev) => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], buy_price: value },
                              }));
                              debouncedInventoryChange(inv.id, { buy_price: value }, edit?.version_buy);
                            }}
                            onFocus={(e) => e.target.select()}
                          />
                        </label>
                        <div className="ProductSearchMail-switchGroup">
                          <input
                            type="checkbox"
                            id={`topSwitch${inv.id}`}
                            checked={edit?.buy_top_switch ?? inv.buy_top_switch ?? false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditValues((prev) => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], buy_top_switch: checked },
                              }));
                              debouncedInventoryChange(inv.id, { buy_top_switch: checked }, edit?.version_buy);
                            }}
                          />
                          <label htmlFor={`topSwitch${inv.id}`} className="ProductSearchMail-checkbox checkbox1">TOP</label>
                        </div>
                      </div>

                      {/* 2行目 */}
                      <div className="ProductSearchMail-line">
                        <span>特別:</span>
                        <label className="ProductSearchMail-label">
                          <input
                            type="number"
                            className="ProductSearchMail-input"
                            value={edit?.buy_specialprice ?? ""}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              setEditValues((prev) => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], buy_specialprice: value },
                              }));
                              debouncedInventoryChange(inv.id, { buy_specialprice: value }, edit?.version_buy);
                            }}
                            onFocus={(e) => e.target.select()}
                          />
                        </label>
                        <div className="ProductSearchMail-switchGroup">
                          <input
                            type="checkbox"
                            id={`buySpecialSwitch${inv.id}`}
                            checked={edit?.buy_specialprice_switch ?? inv.buy_specialprice_switch ?? false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditValues((prev) => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], buy_specialprice_switch: checked },
                              }));
                              debouncedInventoryChange(inv.id, { buy_specialprice_switch: checked }, edit?.version_buy);
                            }}
                          />
                          <label htmlFor={`buySpecialSwitch${inv.id}`} className="ProductSearchMail-checkbox checkbox2">特別</label>
                        </div>
                        <div className="ProductSearchMail-switchGroup">
                          <input
                            type="checkbox"
                            id={`shopOnlySwitch${inv.id}`}
                            checked={edit?.buy_specialprice_shoponly ?? inv.buy_specialprice_shoponly ?? false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setEditValues((prev) => ({
                                ...prev,
                                [inv.id]: { ...prev[inv.id], buy_specialprice_shoponly: checked },
                              }));
                              debouncedInventoryChange(inv.id, { buy_specialprice_shoponly: checked }, edit?.version_buy);
                            }}
                          />
                          <label htmlFor={`shopOnlySwitch${inv.id}`} className="ProductSearchMail-checkbox checkbox3">店限</label>
                        </div>
                      </div>

                      {/* 在庫情報 */}
                      <p className="ProductSearchMail-meta">
                        在庫: {inv.stock} / 通常: {inv.sell_price} / セット: {inv.sell_set_price} 　　
                        <div className="ProductSearchMail-linkButtons">
                        <button className="ProductSearchMail-linkBtn amiami" onClick={() => openAmiamiWindow(product.jan)}>Amiami</button>
                        <button className="ProductSearchMail-linkBtn surugaya" onClick={() => opensurugayaWindow(product, editValues)}>駿河屋</button>
                        <button className="ProductSearchMail-linkBtn amazon" onClick={() => openamazonWindow(product.jan)}>Amazon</button>
                        <button className="ProductSearchMail-linkBtn mercari" onClick={() => openmercariWindow(product.item_name)}>mercari</button>
                        </div>
                      </p>
                    </div>
                  </div>

                  {/* 履歴セクション */}
                  <div className="SearchMail-detailitem-line">
                    <section className="SearchMail-detailitem-AdjustmentList__container">
                      <h3 className="SearchMail-detailitem-AdjustmentList__title">🧾 販売履歴（最新順）</h3>
                      <ul className="SearchMail-detailitem-AdjustmentList__list">
                        {adjustmentsSell.map((adj) => (
                          <li key={adj.id} className="SearchMail-detailitem-AdjustmentList__item">
                            <span className="SearchMail-detailitem-AdjustmentList__date">{adj.date?.slice(0, 16).replace('T', ' ')}</span>
                            <span className={`SearchMail-detailitem-AdjustmentList__qty ${adj.quantity_change > 0 ? 'plus' : 'minus'}`}>
                              {adj.quantity_change > 0 ? '+' : ''}
                              {adj.quantity_change}個
                            </span>
                            <span className="SearchMail-detailitem-AdjustmentList__reason">（{adj.reason}）</span>
                          </li>
                        ))}
                      </ul>
                    </section>

                    <section className="SearchMail-detailitem-AdjustmentList__container">
                      <h3 className="SearchMail-detailitem-AdjustmentList__title">🧾 買取履歴（最新順）</h3>
                      <ul className="SearchMail-detailitem-AdjustmentList__list">
                        {adjustmentsBuy.map((adj) => (
                          <li key={adj.id} className="SearchMail-detailitem-AdjustmentList__item">
                            <span className="SearchMail-detailitem-AdjustmentList__date">{adj.date?.slice(0, 16).replace('T', ' ')}</span>
                            <span className={`SearchMail-detailitem-AdjustmentList__qty ${adj.quantity_change > 0 ? 'plus' : 'minus'}`}>
                              {adj.quantity_change > 0 ? '+' : ''}
                              {adj.quantity_change}個
                            </span>
                            <span className="SearchMail-detailitem-AdjustmentList__reason">（{adj.reason}）</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <p className="ProductSearchMail-loading">🔄 データ読み込み中...</p>
        )
      ) : (
        <>
          <div className="ProductSearchMail-results">
            {Array.isArray(searchResults) && searchResults.map((product) => {
              const inv = Array.isArray(product.store_inventory_set)
                ? product.store_inventory_set.find(i => String(i.store) === String(storeId))
                : null;
              const edit = inv ? editValues[inv.id] : null;

              return (
                <div key={product.id} className="ProductSearchMail-item">
                  <div className="product-search-imgbox" onClick={() => setDetailId(product.id)}>
                    <img
                      src={`/media/product_images/${product.sku}.jpg`}
                      alt={product.item_name}
                      className="ProductSearchMail-image"
                    />
                  </div>
                  <div className="ProductSearchMail-info">
                    <p className="ProductSearchMail-name">{product.item_name}</p>
                    <p className="ProductSearchMail-barcode">
                      JAN: {product.jan || "未登録"}　SKU: {product.sku || "未登録"}
                    </p>
                    {inv ? (
                      <>
                        <div className="ProductSearchMail-line">
                          <span>買取:</span>
                          <label className="ProductSearchMail-label">
                            <input
                              type="number"
                              className="ProductSearchMail-input"
                              value={edit?.buy_price ?? ""}
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                setEditValues((prev) => ({
                                  ...prev,
                                  [inv.id]: { ...prev[inv.id], buy_price: value },
                                }));
                                debouncedInventoryChange(inv.id, { buy_price: value }, edit?.version_buy);
                              }}
                              onFocus={(e) => e.target.select()}
                            />
                          </label>
                          <div className="ProductSearchMail-switchGroup">
                            <input
                              type="checkbox"
                              id={`topSwitch${inv.id}`}
                              checked={edit?.buy_top_switch ?? inv.buy_top_switch ?? false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditValues((prev) => ({
                                  ...prev,
                                  [inv.id]: { ...prev[inv.id], buy_top_switch: checked },
                                }));
                                debouncedInventoryChange(inv.id, { buy_top_switch: checked }, edit?.version_buy);
                              }}
                            />
                            <label htmlFor={`topSwitch${inv.id}`} className="ProductSearchMail-checkbox checkbox1">TOP</label>
                          </div>
                        </div>

                        <div className="ProductSearchMail-line">
                          <span>特別:</span>
                          <label className="ProductSearchMail-label">
                            <input
                              type="number"
                              className="ProductSearchMail-input"
                              value={edit?.buy_specialprice ?? ""}
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                setEditValues((prev) => ({
                                  ...prev,
                                  [inv.id]: { ...prev[inv.id], buy_specialprice: value },
                                }));
                                debouncedInventoryChange(inv.id, { buy_specialprice: value }, edit?.version_buy);
                              }}
                              onFocus={(e) => e.target.select()}
                            />
                          </label>
                          <div className="ProductSearchMail-switchGroup">
                            <input
                              type="checkbox"
                              id={`buySpecialSwitch${inv.id}`}
                              checked={edit?.buy_specialprice_switch ?? inv.buy_specialprice_switch ?? false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditValues((prev) => ({
                                  ...prev,
                                  [inv.id]: { ...prev[inv.id], buy_specialprice_switch: checked },
                                }));
                                debouncedInventoryChange(inv.id, { buy_specialprice_switch: checked }, edit?.version_buy);
                              }}
                            />
                            <label htmlFor={`buySpecialSwitch${inv.id}`} className="ProductSearchMail-checkbox checkbox2">特別</label>
                          </div>
                          <div className="ProductSearchMail-switchGroup">
                            <input
                              type="checkbox"
                              id={`shopOnlySwitch${inv.id}`}
                              checked={edit?.buy_specialprice_shoponly ?? inv.buy_specialprice_shoponly ?? false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditValues((prev) => ({
                                  ...prev,
                                  [inv.id]: { ...prev[inv.id], buy_specialprice_shoponly: checked },
                                }));
                                debouncedInventoryChange(inv.id, { buy_specialprice_shoponly: checked }, edit?.version_buy);
                              }}
                            />
                            <label htmlFor={`shopOnlySwitch${inv.id}`} className="ProductSearchMail-checkbox checkbox3">店限</label>
                          </div>
                        </div>

                        <p className="ProductSearchMail-meta">
                          在庫: {inv.stock} / 通常: {inv.sell_price} / セット: {inv.sell_set_price}
                          <div className="ProductSearchMail-linkButtons">
                            <button className="ProductSearchMail-linkBtn amiami" onClick={() => openAmiamiWindow(product.jan)}>Amiami</button>
                            <button className="ProductSearchMail-linkBtn surugaya" onClick={() => opensurugayaWindow(product, editValues)}>駿河屋</button>
                            <button className="ProductSearchMail-linkBtn amazon" onClick={() => openamazonWindow(product.jan)}>Amazon</button>
                            <button className="ProductSearchMail-linkBtn mercari" onClick={() => openmercariWindow(product.item_name)}>mercari</button>
                          </div>
                        </p>
                      </>
                    ) : (
                      <p className="ProductSearchMail-no-stock">⚠ この店舗に登録がありません</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ProductSearchMail-pagination">
            <button onClick={() => handleSearch(currentPage - 1)} disabled={currentPage === 1}>
              ◀ 前のページ
            </button>
            <span>ページ {currentPage} / {totalPages}</span>
            <button onClick={() => handleSearch(currentPage + 1)} disabled={currentPage >= totalPages}>
              次のページ ▶
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductSearchMail;
